import { useEffect, useRef, useState } from "react";
import { UI_VERSION } from "../../version";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { api } from "@/lib/api";
import JobDashboard from "@/pages/admin/JobDashboard";
import Wizard from "@/pages/admin/Wizard";
import CertDetail from "@/pages/admin/CertDetail";
import FixQueue from "@/pages/admin/FixQueue";

const TABS = [
  { path: "/admin", label: "Job Dashboard", exact: true },
  { path: "/admin/wizard", label: "Setup Wizard" },
  { path: "/admin/fix-queue", label: "Fix Queue" },
];

export default function AdminApp() {
  const { user, refresh } = useAuth();
  const loc = useLocation();
  const [version, setVersion] = useState("");
  const stale = version && version !== UI_VERSION;
  const checking = useRef(false);
  const lost = useRef(false);
  useEffect(() => { api.get("/health").then((r) => setVersion(r.data.version)).catch(() => {}); }, []);
  // v1.20.4: the browser holds ONE session. If it gets replaced (e.g. a free-exam magic link verified in another
  // tab), every admin poll starts failing with 401/403. Re-check the session once, explain it once, and leave the
  // admin app so the pollers unmount — instead of a toast on every poll.
  useEffect(() => {
    const id = api.interceptors.response.use((r) => r, async (err) => {
      const status = err?.response?.status;
      const adminReq = (status === 401 || status === 403) && String(err?.config?.url || "").startsWith("/admin");
      if (adminReq && lost.current) return new Promise(() => {});
      if (adminReq && !checking.current) {
        checking.current = true;
        try {
          const who = await refresh();
          if (!who || !who.is_admin) {
            lost.current = true;
            toast.error(who?.email ? `Admin session ended: this browser is now signed in as ${who.email}. Sign in as an admin to continue.`
                                   : "Admin session ended. Sign in again to continue.", { id: "admin-session-lost", duration: 8000 });
            return new Promise(() => {});  // the admin app is unmounting: swallow this request instead of a second toast
          }
        } finally { checking.current = false; }
      }
      return Promise.reject(err);
    });
    return () => api.interceptors.response.eject(id);
  }, [refresh]);
  if (user === null) return <p className="p-8 text-zinc-500">Loading…</p>;
  if (user === false || !user.is_admin) return <Navigate to="/login" state={{ from: loc.pathname }} />;
  return (
    <div className="App min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-40 bg-white border-b border-zinc-200">
        <div className="px-4 h-12 flex items-center gap-6">
          <Link to="/" className="font-heading font-black">Exam<span className="text-sky-500">Ray</span><span className="ml-2 font-mono text-xs text-zinc-400 font-normal">MACHINE</span></Link>
          <nav className="flex gap-1 text-sm">
            {TABS.map((t) => (
              <Link key={t.path} to={t.path} data-testid={`admin-tab-${t.label.toLowerCase().replace(/ /g, "-")}`}
                    className={`px-3 py-1.5 rounded-md transition-colors duration-150 ${(t.exact ? loc.pathname === t.path : loc.pathname.startsWith(t.path)) ? "bg-sky-50 text-sky-700 font-semibold" : "text-zinc-600 hover:bg-zinc-100"}`}>
                {t.label}
              </Link>
            ))}
          </nav>
          <span className="ml-auto text-xs font-mono text-zinc-400">{user.email}</span>
        </div>
      </header>
      <main className="px-4 py-6">
        <Routes>
          <Route index element={<JobDashboard />} />
          <Route path="wizard" element={<Wizard />} />
          <Route path="certs/:id" element={<CertDetail />} />
          <Route path="certs/:id/stage/:stage" element={<CertDetail />} />
          <Route path="fix-queue" element={<FixQueue />} />
        </Routes>
      </main>
      <footer className="px-4 py-3 border-t border-zinc-200 text-xs font-mono text-zinc-400 flex justify-between">
        <span>ExamRay Machine</span>
        <span data-testid="admin-version" className={stale ? "text-red-600 font-semibold" : ""}>
          {!version ? "…" : stale ? `API ${version} · UI ${UI_VERSION} — stale frontend build: redeploy the static site and hard-refresh` : `${version} (API + UI)`}
        </span>
      </footer>
    </div>
  );
}
